// TradingContext — Core state management for Angel One trading platform
import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import angelOneApi from '../services/angelOneApi';
import wsManager from '../services/angelOneWebSocket';

const TradingContext = createContext();

export const useTrading = () => useContext(TradingContext);

export const TradingProvider = ({ children }) => {
  // Connection state (REST API — the primary connection)
  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  const [connectionError, setConnectionError] = useState(null);
  const [profile, setProfile] = useState(null);
  const [feedToken, setFeedToken] = useState(null);
  const [clientcode, setClientcode] = useState(null);

  // WebSocket state (optional — for live streaming, separate from connection)
  const [wsStatus, setWsStatus] = useState('disconnected');

  // Market data
  const [quotes, setQuotes] = useState({});
  const [indexData, setIndexData] = useState({});

  // Portfolio
  const [positions, setPositions] = useState([]);
  const [holdings, setHoldings] = useState([]);
  const [orderBook, setOrderBook] = useState([]);
  const [tradeBook, setTradeBook] = useState([]);

  // Watchlists
  const [watchlists, setWatchlists] = useState([]);
  const [activeWatchlist, setActiveWatchlist] = useState(0);

  // Algo
  const [strategies, setStrategies] = useState([]);
  const [signals, setSignals] = useState([]);
  const [algoRunning, setAlgoRunning] = useState(false);
  const [algoOverlay, setAlgoOverlay] = useState(null);

  // UI
  const [activeTab, setActiveTab] = useState('market');
  const [orderModal, setOrderModal] = useState(null);
  const [searchOpen, setSearchOpen] = useState(false);

  const refreshIntervalRef = useRef(null);
  const ltpPollingRef = useRef(null);
  const autoReconnectAttempted = useRef(false);

  // ── Connect with tokens ──────────────────────────────────────

  const connectWithTokens = useCallback(async (feedTkn, cc) => {
    setFeedToken(feedTkn);
    setClientcode(cc);
    setConnectionStatus('connected');
    setConnectionError(null);

    // Try WebSocket (optional — failure doesn't break anything)
    try {
      wsManager.connect(feedTkn, cc);
    } catch (e) {
      console.warn('WebSocket connection attempt failed:', e);
    }

    // Load profile
    try {
      const profileResult = await angelOneApi.getProfile();
      if (profileResult.data?.data) {
        setProfile(profileResult.data.data);
      }
    } catch (e) {
      console.warn('Profile fetch failed:', e);
    }
  }, []);

  // ── Login ────────────────────────────────────────────────────

  const login = useCallback(async (credentials) => {
    setConnectionStatus('connecting');
    setConnectionError(null);
    try {
      const result = await angelOneApi.login(credentials);
      const data = result.data;

      if (data.status) {
        await connectWithTokens(data.feedToken, data.clientcode);
        return { success: true };
      } else {
        setConnectionStatus('disconnected');
        setConnectionError(data.message || 'Login failed');
        return { success: false, message: data.message || 'Login failed' };
      }
    } catch (error) {
      setConnectionStatus('disconnected');
      const msg = error.message || 'Connection failed';
      setConnectionError(msg);
      return { success: false, message: msg };
    }
  }, [connectWithTokens]);

  // ── Auto-reconnect on mount ──────────────────────────────────

  useEffect(() => {
    if (autoReconnectAttempted.current) return;
    autoReconnectAttempted.current = true;

    const tryAutoReconnect = async () => {
      try {
        const result = await angelOneApi.checkSession();
        if (result.data?.status) {
          await connectWithTokens(result.data.feedToken, result.data.clientcode);
        }
      } catch (e) {
        // No active session — stay disconnected
      }
    };
    tryAutoReconnect();
  }, [connectWithTokens]);

  // ── Logout ───────────────────────────────────────────────────

  const logout = useCallback(async () => {
    try { await angelOneApi.logout(); } catch (e) { /* ok */ }
    wsManager.disconnect();
    setConnectionStatus('disconnected');
    setWsStatus('disconnected');
    setConnectionError(null);
    setProfile(null);
    setFeedToken(null);
    setClientcode(null);
    setPositions([]);
    setHoldings([]);
    setOrderBook([]);
    setTradeBook([]);
    setQuotes({});
    setIndexData({});
    if (ltpPollingRef.current) clearInterval(ltpPollingRef.current);
  }, []);

  // ── Data Refresh ─────────────────────────────────────────────

  const refreshPortfolio = useCallback(async () => {
    if (connectionStatus !== 'connected') return;
    try {
      const [posResult, holdResult, orderResult, tradeResult] = await Promise.allSettled([
        angelOneApi.getPositions(),
        angelOneApi.getHoldings(),
        angelOneApi.getOrderBook(),
        angelOneApi.getTradeBook(),
      ]);

      if (posResult.status === 'fulfilled' && posResult.value.data?.data) {
        setPositions(Array.isArray(posResult.value.data.data) ? posResult.value.data.data : []);
      }
      if (holdResult.status === 'fulfilled' && holdResult.value.data?.data) {
        setHoldings(Array.isArray(holdResult.value.data.data) ? holdResult.value.data.data : []);
      }
      if (orderResult.status === 'fulfilled' && orderResult.value.data?.data) {
        setOrderBook(Array.isArray(orderResult.value.data.data) ? orderResult.value.data.data : []);
      }
      if (tradeResult.status === 'fulfilled' && tradeResult.value.data?.data) {
        setTradeBook(Array.isArray(tradeResult.value.data.data) ? tradeResult.value.data.data : []);
      }
    } catch (e) {
      console.error('Portfolio refresh failed:', e);
    }
  }, [connectionStatus]);

  // ── WebSocket Tick Handler (optional — doesn't affect connectionStatus) ──

  useEffect(() => {
    const unsubTick = wsManager.on('tick', (tick) => {
      setQuotes(prev => ({
        ...prev,
        [`${tick.exchange}_${tick.token}`]: {
          ...prev[`${tick.exchange}_${tick.token}`],
          ...tick,
          updatedAt: Date.now(),
        },
      }));
    });

    // WebSocket events only update wsStatus, NOT connectionStatus
    const unsubConnect = wsManager.on('connected', () => {
      setWsStatus('connected');
    });

    const unsubDisconnect = wsManager.on('disconnected', () => {
      setWsStatus('disconnected');
    });

    const unsubOrder = wsManager.on('orderUpdate', () => {
      refreshPortfolio();
    });

    return () => {
      unsubTick();
      unsubConnect();
      unsubDisconnect();
      unsubOrder();
    };
  }, [refreshPortfolio]);

  // ── REST API LTP Polling (fallback when WebSocket is down) ───

  useEffect(() => {
    if (connectionStatus !== 'connected') {
      if (ltpPollingRef.current) clearInterval(ltpPollingRef.current);
      return;
    }

    // Poll LTP for subscribed tokens every 5 seconds via REST API
    const pollLTP = async () => {
      const keys = Object.keys(quotes);
      if (keys.length === 0) return;

      // Group tokens by exchange
      const exchangeTokens = {};
      keys.forEach(key => {
        const [exchange, token] = key.split('_');
        if (!exchangeTokens[exchange]) exchangeTokens[exchange] = [];
        exchangeTokens[exchange].push(token);
      });

      try {
        const mode = 'LTP';
        const exchangeTokensParam = exchangeTokens;
        const result = await angelOneApi.getLTP({ exchange: 'NSE', tradingsymbol: '', symboltoken: '' });
        // LTP API returns for individual tokens; batch handled differently
        // For simplicity, update from portfolio/positions if available
      } catch (e) {
        // Silent — polling failure is not critical
      }
    };

    // Only poll if WebSocket is not connected
    if (wsStatus !== 'connected') {
      ltpPollingRef.current = setInterval(pollLTP, 10000);
    }

    return () => {
      if (ltpPollingRef.current) clearInterval(ltpPollingRef.current);
    };
  }, [connectionStatus, wsStatus, quotes]);

  // Auto-refresh portfolio every 30s when connected
  useEffect(() => {
    if (connectionStatus === 'connected') {
      refreshPortfolio();
      refreshIntervalRef.current = setInterval(refreshPortfolio, 30000);
    }
    return () => {
      if (refreshIntervalRef.current) clearInterval(refreshIntervalRef.current);
    };
  }, [connectionStatus, refreshPortfolio]);

  // ── Order Actions ────────────────────────────────────────────

  const placeOrder = useCallback(async (orderData) => {
    const result = await angelOneApi.placeOrder(orderData);
    await refreshPortfolio();
    return result.data;
  }, [refreshPortfolio]);

  const modifyOrder = useCallback(async (orderData) => {
    const result = await angelOneApi.modifyOrder(orderData);
    await refreshPortfolio();
    return result.data;
  }, [refreshPortfolio]);

  const cancelOrder = useCallback(async (orderId, variety) => {
    const result = await angelOneApi.cancelOrder({ variety, orderid: orderId });
    await refreshPortfolio();
    return result.data;
  }, [refreshPortfolio]);

  // ── Quote Helpers ────────────────────────────────────────────

  const getQuote = useCallback((exchange, token) => {
    return quotes[`${exchange}_${token}`] || null;
  }, [quotes]);

  const subscribeTicks = useCallback((tokens, mode) => {
    // Add to quotes map so REST polling picks them up
    const newQuotes = {};
    tokens.forEach(t => {
      const key = `${t.exchange}_${t.token}`;
      newQuotes[key] = quotes[key] || { token: t.token, exchange: t.exchange };
    });
    setQuotes(prev => ({ ...prev, ...newQuotes }));
    // Also try WebSocket subscription
    wsManager.subscribe(tokens, mode);
  }, [quotes]);

  const unsubscribeTicks = useCallback((tokens) => {
    wsManager.unsubscribe(tokens);
  }, []);

  // ── Context Value ────────────────────────────────────────────

  const value = {
    // Connection
    connectionStatus, connectionError, wsStatus,
    profile, feedToken, clientcode,
    login, logout,

    // Market data
    quotes, indexData, getQuote,
    subscribeTicks, unsubscribeTicks,

    // Portfolio
    positions, holdings, orderBook, tradeBook,
    refreshPortfolio,

    // Orders
    placeOrder, modifyOrder, cancelOrder,
    orderModal, setOrderModal,

    // Watchlists
    watchlists, setWatchlists,
    activeWatchlist, setActiveWatchlist,

    // Algo
    strategies, setStrategies,
    signals, setSignals,
    algoRunning, setAlgoRunning,
    algoOverlay, setAlgoOverlay,

    // UI
    activeTab, setActiveTab,
    searchOpen, setSearchOpen,
  };

  return (
    <TradingContext.Provider value={value}>
      {children}
    </TradingContext.Provider>
  );
};
